import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Cell, ConversationStatus, Result } from './styled';
import { getStatus, UNATTEMPTED } from './utils';
import useStatusCellActions from './hooks/use-status-cell-actions';
import StatusCellMenu from './status-cell-menu';
import {
  studentStatus as status,
  isDefined,
  getContextCoords
} from '../../helpers/utils';
import { getActivityReportById } from '../../containers/course-assign/course-assign-actions';
import InstructionHubFeedback from '../instruction-hub-feedback';
import InstructionHubMessage, {
  INSTRUCTION_HUB_MESSAGE_CONTEXTS
} from '../instruction-hub-message';
import useInstructionHubMenuGuard from '../instruction-hub-message/use-instruction-hub-menu-guard';
import { isInstructionHubBlockedAssignment } from '../../helpers/instruction-hub/instruction-hub-assignments';
import ContextModal, { ContextModalWidth } from '../context-modal';
import OutsideClickHandler from '../../helpers/hoc/close/outside-click-handler';
import {
  UPDATE_STUDENT_ASSIGNMENT,
  SELECT_EQUAL_LESS_THAN_CELLS,
  UPDATE_STUDENT_ASSIGNMENTS_SELECTED
} from '../../containers/course-assign/course-assign-types';
import { activateModal } from '../../containers/modal-manager/modal-manager-actions';
import { viewResultsModal } from '../results-modal/results.modal';

const StatusCell = (props) => {
  const {
    i18n,
    studentInfo,
    isCollapsed,
    hasResults,
    resultAverage,
    hasAssignments,
    activities,
    selectedCells,
    activityName,
    currentStudent,
    datePreference,
    courseId,
    classroomId,
    groupId,
    index,
    isTopicInstructionHubBlocked
  } = props;

  const dispatch = useDispatch();
  const [isModalOpen, setModalOpen] = useState(false);
  const { unassign, reassign } = useStatusCellActions({
    i18n,
    studentInfo,
    datePreference,
    courseId,
    classroomId,
    groupId,
    onComplete: () => setModalOpen(false)
  });
  const [positionX, setPositionX] = useState(0);
  const [positionY, setPositionY] = useState(0);
  const [positionArrow, setPositionArrow] = useState(0);
  const [flowUp, setShouldFlowUp] = useState(false);

  let {
    assignment,
    result,
    isSelected,
    isFiltered,
    activityId,
    taskType,
    encryptedUserProfileId
  } = useSelector(
    (state) => state.CourseAssignReducer?.assignments.studentAssignments[index]
  );

  if (isCollapsed) {
    ({
      assignment,
      result,
      isSelected,
      activityId,
      isFiltered,
      taskType,
      encryptedUserProfileId
    } = studentInfo);
  }

  const instructionHubBlockedLookup = useSelector(
    (state) => state.CourseAssignReducer.instructionHubBlockedLookup
  );

  useEffect(() => {
    if (isModalOpen) {
      window.addEventListener('scroll', setPosition, true);
    }

    return () => window.removeEventListener('scroll', setPosition, true);
  });

  const shouldBeSelected = isCollapsed ? selectedCells > 0 : isSelected;

  const [isCellSelected, setIsCellSelected] = useState(shouldBeSelected);

  useEffect(() => {
    if (shouldBeSelected !== isCellSelected) {
      setIsCellSelected(shouldBeSelected);
    }
  }, [shouldBeSelected, isCellSelected]);

  const score = result && result.score;

  const isCellInstructionHubBlocked = isInstructionHubBlockedAssignment(
    { encryptedUserProfileId, activityId, taskType },
    instructionHubBlockedLookup
  );
  const isInstructionHubBlocked =
    !isFiltered &&
    (isCollapsed ? isTopicInstructionHubBlocked : isCellInstructionHubBlocked);
  const isInstructionHubReadOnly =
    isInstructionHubBlocked && (isCollapsed || !isDefined(score));
  const {
    isInstructionHubMessageShown,
    hideInstructionHubMessage,
    runUnlessInstructionHubBlocked,
    instructionHubContextModalProps
  } = useInstructionHubMenuGuard(isInstructionHubBlocked);

  const cellRef = useRef(null);

  const assignStatus = isDefined(assignment?.assignStatus)
    ? assignment?.assignStatus
    : status[UNATTEMPTED];

  const isAssigned =
    assignStatus === status.ASSIGNED ||
    assignStatus === status.INPROGRESS ||
    assignStatus === status.PAST_DUE_DATE ||
    hasAssignments;

  const resultSummary = () => {
    if (resultAverage >= 0 && isCollapsed && hasResults) {
      return (
        <Result
          isFiltered={isFiltered}
          hasResults={hasResults}
          isCollapsed={isCollapsed}
          percentage={score}
          isAssigned={isAssigned}
        >{`${resultAverage.toFixed(1)}%`}</Result>
      );
    } else if (isDefined(score)) {
      return (
        <Result
          isFiltered={isFiltered}
          hasResults={hasResults}
          isCollapsed={isCollapsed}
          percentage={score}
          isAssigned={isAssigned}
        >{`${score}%`}</Result>
      );
    }
    return null;
  };

  const selectCell = () => {
    setIsCellSelected(!isCellSelected);
    dispatch({
      type: UPDATE_STUDENT_ASSIGNMENT,
      index
    });
    setModalOpen(false);
  };

  const cellClickHandler = () => {
    if (isFiltered) {
      return;
    }
    if (isCollapsed) {
      setIsCellSelected(!isCellSelected);
      dispatch({
        type: UPDATE_STUDENT_ASSIGNMENTS_SELECTED,
        payload: {
          activities,
          encryptedUserProfileId
        }
      });
    } else if (isDefined(score)) {
      showContextModal();
    } else {
      selectCell();
    }
  };

  // Select all less than or equal to cell result
  const selectAllBelow = () => {
    if (!isDefined(score)) {
      return;
    }
    dispatch({
      type: SELECT_EQUAL_LESS_THAN_CELLS,
      result: score,
      currentStudent
    });
    setModalOpen(false);
  };

  function setPosition() {
    const { posX, posY, posArrow, reverseArrow } = getContextCoords(
      cellRef.current.getBoundingClientRect(),
      ContextModalWidth
    );
    setPositionY(posY);
    setPositionX(posX);
    setPositionArrow(posArrow);
    setShouldFlowUp(reverseArrow);
  }

  function showContextModal() {
    setPosition();
    hideInstructionHubMessage();
    setModalOpen(!isModalOpen);
  }

  function getAssignmentDates() {
    if (assignment && assignment?.startDate) {
      return `${i18n.t('Generic.Assigned')`Assigned`} ${moment(
        assignment.startDate
      ).format('DD-MMM')} - ${
        assignment?.dueDate
          ? moment(assignment.dueDate).format('DD-MMM')
          : i18n.t('Generic.NoDueDate')`no due date`
      }`;
    }

    return moment(result?.completionDate).format('DD-MMM');
  }

  const viewResults = () => {
    dispatch(
      getActivityReportById(
        classroomId,
        encryptedUserProfileId,
        activityId,
        taskType
      )
    );
    dispatch(activateModal(viewResultsModal(i18n, isAssigned)));
  };

  function RenderHandler(renderProps) {
    return isDefined(score) ? (
      <OutsideClickHandler
        callback={() => setModalOpen(false)}
        forButton={`StatusCell`}
      >
        {renderProps?.children}{' '}
      </OutsideClickHandler>
    ) : (
      <>{renderProps?.children}</>
    );
  }

  return (
    <RenderHandler>
      <InstructionHubFeedback
        i18n={i18n}
        context={
          isCollapsed
            ? INSTRUCTION_HUB_MESSAGE_CONTEXTS.TOPIC
            : INSTRUCTION_HUB_MESSAGE_CONTEXTS.CELL
        }
        isBlocked={isInstructionHubReadOnly}
      >
        <Cell
          data-testid={`StatusCell_${encryptedUserProfileId}_${activityId}_${taskType}`}
          onClick={cellClickHandler}
          status={getStatus(assignStatus)}
          percentage={isCollapsed ? resultAverage : score}
          key={`statusCell_${activityId}_${taskType}`}
          isSelected={isCellSelected}
          isCollapsed={isCollapsed}
          isAssigned={isAssigned}
          hasResults={hasResults}
          isFiltered={isFiltered}
          isCellSelected={isCellSelected}
          isInstructionHubBlocked={isInstructionHubBlocked}
          isInstructionHubReadOnly={isInstructionHubReadOnly}
        >
          {
            <>
              <ConversationStatus ref={cellRef}>
                {resultSummary()}
              </ConversationStatus>
              {isModalOpen ? (
                <ContextModal
                  show={isModalOpen}
                  flowUp={flowUp}
                  positionArrow={positionArrow}
                  positionX={positionX}
                  positionY={positionY}
                  {...instructionHubContextModalProps}
                >
                  {isInstructionHubMessageShown ? (
                    <InstructionHubMessage
                      i18n={i18n}
                      context={INSTRUCTION_HUB_MESSAGE_CONTEXTS.CELL}
                      onOpen={() => setModalOpen(false)}
                    />
                  ) : (
                    <StatusCellMenu
                      i18n={i18n}
                      studentName={`${currentStudent.firstName} ${currentStudent.lastName}`}
                      activityName={activityName}
                      assignmentDates={getAssignmentDates()}
                      attempts={result?.attempts}
                      score={score}
                      hasAssignment={isDefined(studentInfo.assignment)}
                      isInstructionHubBlocked={isInstructionHubBlocked}
                      onSelectCell={() =>
                        runUnlessInstructionHubBlocked(selectCell)
                      }
                      onSelectAllBelow={() =>
                        runUnlessInstructionHubBlocked(selectAllBelow)
                      }
                      onUnassign={() =>
                        runUnlessInstructionHubBlocked(unassign)
                      }
                      onReassign={() =>
                        runUnlessInstructionHubBlocked(reassign)
                      }
                      onViewResults={viewResults}
                    />
                  )}
                </ContextModal>
              ) : null}
            </>
          }
        </Cell>
      </InstructionHubFeedback>
    </RenderHandler>
  );
};

StatusCell.propTypes = {
  studentInfo: PropTypes.object,
  activityName: PropTypes.string,
  isCollapsed: PropTypes.bool,
  hasResults: PropTypes.bool,
  hasAssignments: PropTypes.bool,
  resultAverage: PropTypes.number,
  activities: PropTypes.array,
  selectedCells: PropTypes.number,
  i18n: PropTypes.object,
  currentStudent: PropTypes.object.isRequired,
  datePreference: PropTypes.array.isRequired,
  courseId: PropTypes.number.isRequired,
  classroomId: PropTypes.number.isRequired,
  index: PropTypes.number,
  groupId: PropTypes.number.isRequired,
  isTopicInstructionHubBlocked: PropTypes.bool
};

StatusCell.defaultProps = {
  isCollapsed: false,
  hasAssignments: false,
  activityIds: [],
  selectedCells: 0,
  index: 0,
  isTopicInstructionHubBlocked: false
};

export default StatusCell;



// WEBPACK FOOTER //
// ./components/status-cell/index.js